import React from 'react';
import {
  BadgeCheck,
  Building2,
  Mail,
  MessagesSquare,
  Newspaper,
  ShieldCheck,
  Users
} from 'lucide-react';
import { PageHero } from '../components/shared/PageHero';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import {
  PolicyBulletList,
  PolicyCardGrid,
  PolicyDetailList,
  PolicyLastUpdated,
  PolicyRelatedLinks,
  PolicySection
} from '../components/legal/PolicyPrimitives';
import { useSEO } from '../hooks/useSEO';

const LAST_UPDATED = '11 de mayo de 2026';

const summaryCards = [
  {
    label: 'Responsable',
    value: 'ZAMELUX GREEN SL',
    description: 'Entidad responsable del tratamiento de los datos personales vinculados a este sitio.'
  },
  {
    label: 'Finalidades',
    value: 'Atención y gestión',
    description: 'Consultas, relación contractual, soporte, comunicaciones y gestión de comunidades sociales.'
  },
  {
    label: 'Base jurídica',
    value: 'Consentimiento y contrato',
    description: 'Tratamientos legitimados por el consentimiento del usuario y/o la relación contractual.'
  },
  {
    label: 'Derechos',
    value: 'Acceso y oposición',
    description: 'Incluye rectificación, supresión, limitación, portabilidad y retirada del consentimiento.'
  }
];

const controllerDetails = [
  {
    label: 'Responsable del tratamiento',
    value: 'ZAMELUX GREEN SL'
  },
  {
    label: 'CIF',
    value: 'B67255497'
  },
  {
    label: 'Domicilio social',
    value: 'Carrer de la Riera Roja, 12, 08830 Sant Boi de Llobregat, Barcelona'
  },
  {
    label: 'Correo general',
    value: (
      <a
        href="mailto:info@zwheel.com"
        className="font-medium text-blue-600 underline underline-offset-4 hover:text-blue-700"
      >
        info@zwheel.com
      </a>
    )
  },
  {
    label: 'Privacidad y derechos',
    value: (
      <a
        href="mailto:info@zwheel-shop.com"
        className="font-medium text-blue-600 underline underline-offset-4 hover:text-blue-700"
      >
        info@zwheel-shop.com
      </a>
    )
  },
  {
    label: 'Ámbito',
    value: 'Sitio web, formularios, comunicaciones comerciales y perfiles sociales oficiales de la marca'
  }
];

const dataRetentionTerms = [
  'Los datos personales se incorporarán a los sistemas de tratamiento titularidad de ZAMELUX GREEN SL únicamente para fines determinados, explícitos y legítimos.',
  'La conservación se limitará al tiempo estrictamente necesario para atender la finalidad correspondiente y cumplir las obligaciones legales que resulten exigibles en cada caso.',
  'La empresa tratará los datos de forma lícita, leal, transparente, adecuada, pertinente, limitada, exacta y actualizada, adoptando medidas razonables para rectificar o suprimir los datos inexactos sin dilación.'
];

const rightsTerms = [
  'Derecho de acceso a sus datos personales y a la información relativa al tratamiento realizado.',
  'Derecho de rectificación de los datos inexactos o incompletos.',
  'Derecho de supresión cuando los datos ya no sean necesarios o proceda retirar el consentimiento.',
  'Derecho a la limitación del tratamiento en los supuestos legalmente previstos.',
  'Derecho a la portabilidad de los datos facilitados en un formato estructurado y de uso común.',
  'Derecho de oposición al tratamiento y derecho a retirar el consentimiento en cualquier momento.'
];

const socialMediaTerms = [
  'ZAMELUX GREEN SL gestiona perfiles oficiales en redes sociales con la finalidad principal de dar a conocer productos, servicios, novedades y acciones promocionales de la marca.',
  'Cuando el usuario sigue o interactúa con dichos perfiles, consiente el tratamiento de los datos públicos disponibles en su perfil dentro de la propia plataforma social.',
  'La empresa podrá acceder al nombre de contacto y a otra información pública del perfil para responder mensajes, replicar publicaciones de la página en el perfil del usuario o comunicar novedades a través de las herramientas de la red social.',
  'Los contenidos publicados por terceros en estos perfiles seguirán siendo responsabilidad de quien los publique. ZAMELUX GREEN SL podrá retirar contenidos que vulneren la ley, la buena fe, la propiedad intelectual o el derecho a la propia imagen.',
  'Las imágenes, comentarios y demás materiales subidos a la red social permanecerán alojados en la plataforma correspondiente y se regirán también por sus propias políticas de privacidad.'
];

const marketingTerms = [
  'El envío de comunicaciones comerciales por correo electrónico u otros medios equivalentes requerirá el consentimiento expreso previo del interesado, salvo en los supuestos permitidos por la normativa aplicable.',
  'Si el usuario se suscribe al boletín o acepta recibir promociones, ZAMELUX GREEN SL utilizará los datos necesarios para gestionar dichas comunicaciones de acuerdo con el consentimiento otorgado.',
  'La baja podrá ejercerse en cualquier momento mediante el enlace incluido en cada comunicación o escribiendo a la dirección de contacto habilitada para privacidad.',
  'Una vez solicitada la baja, la dirección de correo quedará bloqueada para esa finalidad, salvo que exista otra base jurídica válida o el usuario otorgue un nuevo consentimiento expreso.'
];

const complaintTerms = [
  'El interesado podrá presentar las reclamaciones que considere oportunas ante la autoridad de control competente en materia de protección de datos.',
  'Para una atención previa y más ágil, ZAMELUX GREEN SL recomienda dirigir primero la solicitud o incidencia a los canales de contacto indicados en esta política.'
];

const purposeCards = [
  {
    title: 'Consultas y atención al cliente',
    description: 'Responder formularios, solicitudes de contacto, incidencias y comunicaciones relacionadas con los servicios de la marca.',
    legalBasis: 'Consentimiento del usuario.'
  },
  {
    title: 'Relación contractual y postventa',
    description: 'Facilitar, agilizar y cumplir los compromisos derivados de compras, garantías, soporte y otras relaciones comerciales.',
    legalBasis: 'Ejecución de la relación contractual.'
  },
  {
    title: 'Boletines y promociones',
    description: 'Enviar comunicaciones comerciales sobre productos o promociones cuando el usuario haya prestado su autorización expresa.',
    legalBasis: 'Consentimiento expreso.'
  },
  {
    title: 'Gestión de redes sociales',
    description: 'Administrar la comunidad social de la marca, responder interacciones y comunicar publicaciones o promociones dentro de la plataforma.',
    legalBasis: 'Consentimiento manifestado al seguir e interactuar con el perfil.'
  }
];

export default function PrivacyPolicy() {
  useSEO({
    title: 'Política de privacidad',
    description: 'Consulta cómo trata ZAMELUX GREEN SL los datos personales recogidos a través del sitio web, formularios, redes sociales y comunicaciones comerciales.',
    keywords: 'politica de privacidad, RGPD, Zamelux, Zwheel, datos personales, redes sociales'
  });

  return (
    <div className="min-h-screen bg-white">
      <PageHero
        tag="Legal"
        title="Política de privacidad"
        subtitle="Cómo tratamos sus datos personales en el sitio web, en los formularios de contacto y en los perfiles sociales de ZWHEEL."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="container-max section-padding">
          <div className="mx-auto max-w-5xl space-y-10">
            <PolicyLastUpdated date={LAST_UPDATED} />

            <AnimatedSection>
              <div className="blog-content-clean">
                <p className="text-lg leading-8 text-gray-700">
                  Esta política resume de forma ordenada los tratamientos de datos personales asociados a la
                  actividad digital de ZAMELUX GREEN SL, incluyendo la navegación por el sitio, la atención a
                  consultas, la relación contractual, las comunicaciones comerciales y la gestión de perfiles
                  oficiales en redes sociales.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.05}>
              <PolicyCardGrid items={summaryCards} />
            </AnimatedSection>

            <PolicySection title="Responsable del tratamiento" icon={Building2} delay={0.1} tone="muted">
              <PolicyDetailList items={controllerDetails} />
            </PolicySection>

            <PolicySection title="Datos, finalidades y bases de legitimación" icon={BadgeCheck} delay={0.15}>
              <p className="mb-6 text-base leading-7 text-gray-700">
                Los datos personales facilitados por el usuario se tratarán únicamente para fines compatibles
                con la relación mantenida con ZAMELUX GREEN SL y con el alcance del consentimiento prestado.
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {purposeCards.map((card) => (
                  <article key={card.title} className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
                    <h3 className="mb-3 text-xl font-semibold text-gray-900">{card.title}</h3>
                    <p className="mb-4 text-base leading-7 text-gray-700">{card.description}</p>
                    <p className="text-sm leading-6 text-gray-600">
                      <strong>Base jurídica:</strong> {card.legalBasis}
                    </p>
                  </article>
                ))}
              </div>
            </PolicySection>

            <PolicySection title="Conservación y calidad de los datos" icon={ShieldCheck} delay={0.2} tone="accent">
              <PolicyBulletList items={dataRetentionTerms} />
            </PolicySection>

            <PolicySection title="Derechos de las personas interesadas" icon={Users} delay={0.25}>
              <PolicyBulletList items={rightsTerms} />
              <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50 p-6">
                <p className="mb-4 text-base leading-7 text-gray-700">
                  Para ejercer cualquiera de estos derechos, puede remitir su solicitud a la dirección postal
                  indicada en esta política o a los siguientes correos:
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <a
                    href="mailto:info@zwheel-shop.com"
                    className="rounded-2xl border border-gray-200 bg-white px-5 py-4 text-base font-medium text-blue-600 underline underline-offset-4 hover:text-blue-700"
                  >
                    info@zwheel-shop.com
                  </a>
                  <a
                    href="mailto:info@zwheel.com"
                    className="rounded-2xl border border-gray-200 bg-white px-5 py-4 text-base font-medium text-blue-600 underline underline-offset-4 hover:text-blue-700"
                  >
                    info@zwheel.com
                  </a>
                </div>
              </div>
            </PolicySection>

            <PolicySection title="Privacidad en redes sociales" icon={MessagesSquare} delay={0.3}>
              <PolicyBulletList items={socialMediaTerms} />
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <a
                  href="https://www.facebook.com/help/323540651073243/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-base font-medium text-blue-600 underline underline-offset-4 hover:text-blue-700"
                >
                  Política de privacidad de Facebook
                </a>
                <a
                  href="https://privacycenter.instagram.com/policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-base font-medium text-blue-600 underline underline-offset-4 hover:text-blue-700"
                >
                  Política de privacidad de Instagram
                </a>
              </div>
            </PolicySection>

            <PolicySection title="Comunicaciones comerciales y boletines" icon={Newspaper} delay={0.35} tone="muted">
              <PolicyBulletList items={marketingTerms} />
            </PolicySection>

            <PolicySection title="Reclamaciones y contacto" icon={Mail} delay={0.4}>
              <PolicyBulletList items={complaintTerms} />
              <p className="mt-6 text-base leading-7 text-gray-700">
                El envío de formularios, solicitudes o correos relacionados con privacidad implica que el
                usuario declara haber leído esta política y acepta la cláusula informativa aplicable a cada caso.
              </p>
            </PolicySection>

            <PolicyRelatedLinks
              title="Documentos relacionados"
              links={[
                { label: 'Aviso legal y condiciones de uso', href: '/legal/terms' },
                { label: 'Política de cookies', href: '/legal/cookies' },
                { label: 'Contacto', href: '/contacto' }
              ]}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
